/**
 * Geocodificador especializado para Torres de Comunicación en Andalucía
 * 
 * Fusiona tres fuentes de datos para máxima cobertura:
 * - DERA G9: 3.421 torres genéricas (oficial IECA) - Score 95
 * - OSM Overpass: ~1.875 torres tower:type=communication - Score 75
 * - NGA Topónimos: Variable ("Cerro/Antena" típicos) - Score 60
 * 
 * IMPORTANTE: Estas NO son antenas de operadores (Movistar/Vodafone/Orange).
 * Los datos de operadores están en MINETUR con acceso restringido.
 * 
 * Cobertura estimada: ~60-70% de infraestructura real de telecomunicaciones.
 * 
 * Fuentes:
 * - WFS DERA G9: https://www.ideandalucia.es/services/DERA_g9_infraestructuras_energia/wfs
 * - OSM Overpass: https://overpass-api.de/api/interpreter
 * - WFS NGA: https://www.ideandalucia.es/wfs-nga/services
 * 
 * @module services/geocoding/specialized
 * @version 1.0.0
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import { WFSBaseGeocoder } from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import { OverpassGeocoder, OVERPASS_TIMEOUT_FILA_MS, type OverpassSearchOptions } from './OverpassGeocoder';
import { NGAGeocoder, ToponymType, type NGASearchOptions } from './NGAGeocoder';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';
import type { GeocodingResult as _TypedResult } from '../../../types/errors';
import type { GeocodedCoordinates as _GeocodedCoordinates } from '../generic/CartoCiudadGeocoder';
import { 
  createErrorContext as _createErrorContext, 
  createError as _createError, 
  success as _success, 
  failure as _failure,
} from '../../../types/errors';
// F3.T3: LocalDataService para obtener centroide municipal como fallback
import { searchLocal, type LocalFeature as _LocalFeature } from '../../../lib/LocalDataService';

/**
 * F3.T3: Operadores de telefonía móvil conocidos en España
 * Usado para detectar búsquedas aproximadas de antenas de operador
 * cuando el documento PTEL dice "Antena Movistar" pero no hay datos
 * oficiales disponibles (CNAF es restringido).
 */
const MOBILE_OPERATORS = [
  'movistar', 'telefonica', 'telefónica',
  'vodafone',
  'orange', 'jazztel',
  'masmovil', 'másmovil', 'más móvil', 'mas movil', 'másmóvil',
  'yoigo', 'simyo', 'pepephone', 'lowi',
  'digi', 'o2', 'amena',
  'telecable', 'euskaltel', 'r cable',
  'finetwork', 'republica movil', 'república móvil'
];

/**
 * Tipos de torres de comunicación detectables
 */
export enum TowerType {
  /** Torres genéricas DERA (sin clasificar) */
  GENERIC = 'GENERIC',
  
  /** Torres de telefonía móvil (detectado por contexto OSM) */
  MOBILE = 'MOBILE',
  
  /** Torres de televisión/TDT */
  TV = 'TV',
  
  /** Torres de radio/FM */
  RADIO = 'RADIO',
  
  /** Repetidores diversos */
  REPEATER = 'REPEATER',
  
  /** Ubicación por topónimo (Cerro típico de antenas) */
  TOPONYM = 'TOPONYM'
}

/**
 * Opciones de búsqueda específicas para torres
 */
export interface TowerSearchOptions extends WFSSearchOptions {
  /** Tipo preferido de torre */
  towerType?: TowerType;
  
  /** Incluir búsqueda en OSM si no hay resultados DERA */
  includeOSM?: boolean;
  
  /** Incluir búsqueda en NGA como fallback */
  includeNGA?: boolean;
  
  /** BBOX para filtro espacial [minX, minY, maxX, maxY] EPSG:25830 */
  bbox?: [number, number, number, number];
  
  /** F3.T3: Código INE del municipio para fallback a centroide */
  codMun?: string;
}

/**
 * Resultado extendido con metadata de fuente
 */
export interface TowerGeocodingResult extends GeocodingResult {
  /** Tipo de torre detectado */
  towerType: TowerType;
  
  /** Fuente original (DERA/OSM/NGA) */
  dataSource: 'DERA_G9' | 'OSM_OVERPASS' | 'NGA_TOPONYM';
  
  /** ID DERA si aplica */
  idDera?: string;
  
  /** ID OSM si aplica */
  osmId?: number;
}

// ==========================================================================
// F3.T3.3 - TIPOS PARA DISTRIBUCIÓN INTELIGENTE DE MÚLTIPLES ANTENAS
// ==========================================================================

/**
 * Información de una infraestructura TELECOM a geocodificar
 */
export interface TelecomInfrastructure {
  /** ID único de la infraestructura */
  id: string;
  /** Nombre de la infraestructura (ej: "Antena Movistar") */
  name: string;
  /** Dirección o referencia adicional (ej: "Cerro del Castillo") */
  address?: string;
  /** Operador detectado (si aplica) */
  operator?: string;
}

/**
 * Resultado de geocodificación distribuida con metadata adicional
 */
export interface DistributedGeocodingResult extends TowerGeocodingResult {
  /** Indica si la ubicación es compartida con otras infraestructuras */
  sharedLocation?: boolean;
  /** IDs de otras infraestructuras que comparten ubicación */
  sharedWith?: string[];
  /** Orden de asignación en la distribución */
  assignmentOrder?: number;
  /** Distancia a torre más cercana si se usó geocodificación por nombre */
  distanceToMatchedTower?: number;
}

/**
 * Geocodificador de Torres de Comunicación con cascada DERA → OSM → NGA
 * 
 * Estrategia de cascada:
 * 1. DERA G9 (oficial, EPSG:25830 nativo) - Score 95
 * 2. OSM Overpass (complemento con tipo específico) - Score 75
 * 3. NGA Topónimos (fallback "Cerro/Antena") - Score 60
 * 
 * @example
 * ```typescript
 * const geocoder = new TorresComGeocoder();
 * const result = await geocoder.geocodeWithCascade({
 *   name: 'Antena Telefonía',
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * // result.dataSource indica de dónde viene el dato
 * ```
 */
export class TorresComGeocoder extends WFSBaseGeocoder {
  
  /** Geocodificador OSM para fallback */
  private osmGeocoder: OverpassGeocoder;
  
  /** Geocodificador NGA para fallback final */
  private ngaGeocoder: NGAGeocoder;

  constructor(config?: Partial<SpecializedGeocoderConfig>) {
    super(config);
    this.osmGeocoder = new OverpassGeocoder();
    this.ngaGeocoder = new NGAGeocoder();
  }

  /**
   * Configuración específica para WFS DERA G9 Antenas
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: 'https://www.ideandalucia.es/services/DERA_g9_infraestructuras_energia/wfs',
      layerName: 'g09_26_Antena',
      fuzzyThreshold: 0.40, // Threshold medio (nombres genéricos)
      timeout: 15000,
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Parsea feature GeoJSON de DERA G9 Antenas
   * 
   * Estructura DERA G9:
   * {
   *   type: "Feature",
   *   geometry: { type: "MultiPoint", coordinates: [[x, y]] },
   *   properties: {
   *     id_dera: "G09_26_0001234",
   *     nombre: "Antena Telecomunicaciones",
   *     tipo: "Telecomunicaciones"
   *   }
   * }
   * 
   * NOTA: DERA G9 NO tiene campo cod_mun, solo coordenadas.
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // DERA G9 usa MultiPoint
      if (!geom || !geom.coordinates) {
        console.warn('Feature torre sin geometría válida:', feature.id);
        return null;
      }

      // Extraer coordenadas (MultiPoint → primer punto)
      let x: number, y: number;
      
      if (geom.type === 'MultiPoint' && Array.isArray(geom.coordinates) && Array.isArray((geom.coordinates as unknown[])[0])) {
        const first = (geom.coordinates as Array<[number, number] | [number, number, number]>)[0];
        [x, y] = first;
      } else if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
        [x, y] = geom.coordinates as [number, number];
      } else {
        console.warn('Geometría no soportada:', geom.type);
        return null;
      }

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      return {
        name: getStringProp(props, 'nombre', 'NOMBRE') || 'Torre Comunicación',
        x,
        y,
        municipality: '', // DERA G9 no tiene municipio, requiere spatial join
        province: '',
        address: '',
        properties: {
          idDera: getStringProp(props, 'id_dera', 'ID_DERA'),
          tipo: getStringProp(props, 'tipo', 'TIPO') || 'Telecomunicaciones',
          ...props
        }
      };
    } catch (error) {
      console.error('Error parseando feature torre:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL para torres de comunicación
   * 
   * NOTA: DERA G9 no tiene campo municipio, usamos BBOX si disponible
   */
  protected buildCQLFilter(options: TowerSearchOptions): string {
    const filters: string[] = [];

    // Filtro BBOX (único filtro espacial disponible para G9)
    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(`BBOX(geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
    }

    // Filtro por nombre si es específico
    if (options.name && options.name.length > 3) {
      filters.push(`nombre ILIKE '%${this.escapeCQL(options.name)}%'`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * F3.T3: Detecta si el nombre contiene un operador móvil conocido
   * 
   * Cuando los documentos PTEL dicen "Antena Movistar" o "Torre Vodafone",
   * no podemos hacer match exacto porque los datos oficiales (CNAF) son
   * restringidos. En su lugar, buscamos cualquier torre móvil cercana.
   * 
   * @param name - Nombre de la infraestructura del PTEL
   * @returns true si contiene operador móvil conocido
   */
  private isMobileOperatorSearch(name: string): boolean {
    const nameLower = name.toLowerCase();
    return MOBILE_OPERATORS.some(op => nameLower.includes(op));
  }

  /**
   * F3.T3: Geocodifica antena de operador móvil de forma aproximada
   * 
   * Estrategia en cascada:
   * 1. Buscar torres OSM en el municipio exacto
   * 2. Si no hay, buscar en radio 10km desde centroide municipal
   * 3. Si aún no hay, usar centroide municipal como fallback (score 35%)
   * 
   * Score según fuente:
   * - Torre en municipio: 55%
   * - Torre en radio 10km: 45%
   * - Centroide municipal: 35%
   * 
   * @param options - Opciones con municipio obligatorio
   * @returns Torre móvil encontrada o aproximación a centroide
   */
  private async geocodeMobileApproximate(
    options: TowerSearchOptions
  ): Promise<TowerGeocodingResult | null> {
    if (!options.municipality) {
      console.warn('[TorresComGeocoder] Municipio requerido para búsqueda aproximada');
      return null;
    }

    try {
      // === NIVEL 1: Buscar torres OSM en el municipio ===
      // ANTMUN: esta consulta corre dentro de la fila (tope de 10 s), así que
      // usa el tiempo de fila (8 s), no el municipal (30 s). Si Overpass cuelga,
      // el corte de CORTEOVP se abre, la L2 vuelve vacía al instante y la fila
      // llega al centroide (L3) en vez de quedarse sin coordenada.
      const allTowers = await this.osmGeocoder.findAllTelecomInfrastructure(
        options.municipality,
        options.province,
        OVERPASS_TIMEOUT_FILA_MS
      );

      if (allTowers.length > 0) {
        // Filtrar solo las que parecen ser de telefonía móvil
        const mobileTowers = allTowers.filter(tower => {
          const category = this.osmGeocoder.classifyTelecomType(tower);
          return category === 'MOBILE' || category === 'UNKNOWN';
        });

        const selectedTower = mobileTowers.length > 0 ? mobileTowers[0] : allTowers[0];
        const { x, y } = this.osmGeocoder.toUTM30(selectedTower.lon, selectedTower.lat);

        console.log(
          `[TorresComGeocoder] L1 Torre municipio: "${options.name}" → "${selectedTower.name}" ` +
          `(${mobileTowers.length} móviles de ${allTowers.length} en ${options.municipality})`
        );

        return {
          x,
          y,
          confidence: 55,
          source: 'OpenStreetMap (aproximado)',
          matchedName: selectedTower.name,
          matchType: 'approximate_mobile_L1',
          municipality: options.municipality,
          province: options.province || '',
          towerType: TowerType.MOBILE,
          dataSource: 'OSM_OVERPASS',
          osmId: selectedTower.id,
          metadata: {
            osmId: selectedTower.id,
            originalSearch: options.name,
            approximationLevel: 'L1_MUNICIPALITY',
            approximationNote: `Antena de operador no disponible. Posición aproximada: "${selectedTower.name}". Verificar in-situ.`,
            towersInMunicipality: allTowers.length,
            mobileTowersFound: mobileTowers.length,
            anonymous: selectedTower.anonymous,
            tags: selectedTower.tags
          }
        };
      }

      // === NIVEL 2: Buscar en radio 10km desde centroide ===
      console.log(`[TorresComGeocoder] L1 sin resultados, intentando L2 radio expandido`);
      
      const centroid = await this.getMunicipalityCentroid(options.municipality, options.codMun);
      
      if (centroid) {
        const nearbyTowers = await this.osmGeocoder.findNearbyTelecom(
          { x: centroid.x, y: centroid.y },
          10000 // 10km de radio
        );

        if (nearbyTowers.length > 0) {
          const nearest = nearbyTowers[0];
          const { x, y } = this.osmGeocoder.toUTM30(nearest.lon, nearest.lat);

          console.log(
            `[TorresComGeocoder] L2 Torre radio 10km: "${options.name}" → "${nearest.name}" ` +
            `(${nearest.distanceMeters}m desde centroide ${options.municipality})`
          );

          return {
            x,
            y,
            confidence: 45, // Score reducido por búsqueda expandida
            source: 'OpenStreetMap (radio 10km)',
            matchedName: nearest.name,
            matchType: 'approximate_mobile_L2',
            municipality: options.municipality,
            province: options.province || '',
            towerType: TowerType.MOBILE,
            dataSource: 'OSM_OVERPASS',
            osmId: nearest.id,
            metadata: {
              osmId: nearest.id,
              originalSearch: options.name,
              approximationLevel: 'L2_RADIUS_10KM',
              approximationNote: `No hay torres en ${options.municipality}. Torre más cercana a ${nearest.distanceMeters}m: "${nearest.name}". Verificar in-situ.`,
              distanceFromCentroid: nearest.distanceMeters,
              centroidSource: centroid.name,
              tags: nearest.tags
            }
          };
        }

        // === NIVEL 3: Fallback a centroide municipal ===
        console.log(`[TorresComGeocoder] L2 sin resultados, usando L3 centroide municipal`);

        return {
          x: centroid.x,
          y: centroid.y,
          confidence: 35, // Score mínimo - solo aproximación
          source: 'DERA Municipal (centroide)',
          matchedName: centroid.name,
          matchType: 'centroid_fallback_L3',
          municipality: options.municipality,
          province: options.province || '',
          towerType: TowerType.GENERIC,
          dataSource: 'DERA_G9', // Realmente es DERA municipal
          metadata: {
            originalSearch: options.name,
            approximationLevel: 'L3_CENTROID_FALLBACK',
            approximationNote: `Sin torres telecom en OSM. Coordenadas aproximadas al centro urbano (${centroid.name}). ` +
              `REQUIERE VERIFICACIÓN IN-SITU O DATOS CNAF.`,
            centroidSource: centroid.name,
            centroidCoords: { x: centroid.x, y: centroid.y },
            warning: 'BAJA_PRECISION'
          }
        };
      }

      console.log(`[TorresComGeocoder] No se pudo obtener centroide de ${options.municipality}`);
      return null;

    } catch (error) {
      console.error('[TorresComGeocoder] Error en búsqueda aproximada:', error);
      return null;
    }
  }

  /**
   * Geocodifica con cascada DERA → OSM → NGA
   * 
   * F3.T3: Añadida estrategia "Approximate Mobile Tower" para antenas
   * de operadores (Movistar/Vodafone/Orange) que no están en fuentes públicas.
   * 
   * @param options - Opciones de búsqueda
   * @returns Resultado con metadata de fuente
   */
  public async geocodeWithCascade(
    options: TowerSearchOptions
  ): Promise<TowerGeocodingResult | null> {
    const { includeOSM = true, includeNGA = true } = options;
    
    // F3.T3: Si es búsqueda de operador móvil, usar estrategia aproximada primero
    if (options.name && this.isMobileOperatorSearch(options.name)) {
      console.log(`[TorresComGeocoder] Detectado operador móvil en "${options.name}", usando aproximación`);
      
      const approxResult = await this.geocodeMobileApproximate(options);
      if (approxResult) {
        return approxResult;
      }
      // Si falla aproximación, continuar con cascada normal
    }
    
    // 1. Intentar DERA G9 (Score 95)
    const deraResult = await this.geocodeLegacy(options);
    
    if (deraResult && deraResult.confidence >= 70) {
      return {
        ...deraResult,
        towerType: TowerType.GENERIC,
        dataSource: 'DERA_G9',
        idDera: deraResult.metadata?.idDera as string | undefined,
      };
    }

    // 2. Intentar OSM Overpass (Score 75)
    if (includeOSM) {
      const osmResult = await this.geocodeFromOSM(options);
      
      if (osmResult && osmResult.confidence >= 60) {
        return osmResult;
      }
    }

    // 3. Fallback NGA Topónimos (Score 60)
    if (includeNGA) {
      const ngaResult = await this.geocodeFromNGA(options);
      
      if (ngaResult && ngaResult.confidence >= 50) {
        return ngaResult;
      }
    }

    return null;
  }

  /**
   * Geocodifica usando OSM Overpass para torres de comunicación
   */
  private async geocodeFromOSM(
    options: TowerSearchOptions
  ): Promise<TowerGeocodingResult | null> {
    try {
      const osmOptions: OverpassSearchOptions = {
        name: options.name,
        municipality: options.municipality || '',
        province: options.province,
        maxResults: options.maxResults || 10,
      };

      // Query Overpass específica para torres comunicación
      const result = await this.osmGeocoder.geocodeTower(osmOptions);

      if (!result) {
        return null;
      }

      // Detectar tipo de torre por tags OSM
      const towerType = this.detectTowerTypeFromOSM(result);

      return {
        ...result,
        confidence: Math.min(result.confidence, 75), // Cap score OSM
        towerType,
        dataSource: 'OSM_OVERPASS',
        osmId: result.metadata?.osmId as number | undefined,
      };
    } catch (error) {
      console.error('Error geocodificando torre desde OSM:', error);
      return null;
    }
  }

  /**
   * Geocodifica usando NGA para topónimos típicos de antenas
   */
  private async geocodeFromNGA(
    options: TowerSearchOptions
  ): Promise<TowerGeocodingResult | null> {
    try {
      // Buscar topónimos tipo "Cerro" que suelen tener antenas
      const ngaOptions: NGASearchOptions = {
        name: options.name,
        municipality: options.municipality,
        province: options.province,
        toponymType: ToponymType.CERRO,
        maxResults: options.maxResults || 10,
      };

      const result = await this.ngaGeocoder.geocodeLegacy(ngaOptions);

      if (!result) {
        return null;
      }

      return {
        ...result,
        confidence: Math.min(result.confidence, 60), // Cap score NGA
        towerType: TowerType.TOPONYM,
        dataSource: 'NGA_TOPONYM',
      };
    } catch (error) {
      console.error('Error geocodificando torre desde NGA:', error);
      return null;
    }
  }

  /**
   * Detecta tipo de torre basándose en tags OSM
   */
  private detectTowerTypeFromOSM(result: GeocodingResult): TowerType {
    const tags = (result.metadata?.tags || {}) as Record<string, string>;
    const towerType = tags['tower:type'] || '';
    const manMade = tags['man_made'] || '';

    if (towerType.includes('communication') || manMade === 'communications_tower') {
      // Detectar subtipo
      if (tags['communication:mobile_phone']) {
        return TowerType.MOBILE;
      }
      if (tags['communication:television']) {
        return TowerType.TV;
      }
      if (tags['communication:radio']) {
        return TowerType.RADIO;
      }
      return TowerType.REPEATER;
    }

    return TowerType.GENERIC;
  }

  /**
   * Obtiene todas las torres de comunicación de un municipio
   * Combina DERA G9 + OSM para máxima cobertura
   * 
   * @param municipality - Nombre del municipio
   * @param bbox - BBOX del municipio en EPSG:25830
   * @returns Array de features de torres
   */
  public async getAllTowersInMunicipality(
    municipality: string,
    bbox: [number, number, number, number]
  ): Promise<WFSFeature[]> {
    const allFeatures: WFSFeature[] = [];

    // 1. Obtener de DERA G9 (filtro BBOX)
    try {
      const params = this.buildWFSParams({
        name: '',
        bbox,
        maxResults: 500
      });

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      const deraFeatures = this.parseResponse(response.data);
      
      // Marcar origen
      deraFeatures.forEach(f => {
        f.properties.dataSource = 'DERA_G9';
      });
      
      allFeatures.push(...deraFeatures);
    } catch (error) {
      console.error(`Error obteniendo torres DERA de ${municipality}:`, error);
    }

    // 2. Obtener de OSM (complemento)
    try {
      const osmFeatures = await this.osmGeocoder.getTowersInArea(municipality, bbox);
      
      // Marcar origen y convertir a WFSFeature
      osmFeatures.forEach(f => {
        allFeatures.push({
          name: f.name,
          x: f.x,
          y: f.y,
          municipality,
          province: '',
          address: '',
          properties: {
            ...f.properties,
            dataSource: 'OSM_OVERPASS',
            osmId: f.id
          }
        });
      });
    } catch (error) {
      console.error(`Error obteniendo torres OSM de ${municipality}:`, error);
    }

    return allFeatures;
  }

  /**
   * F3.T3: Obtiene el centroide del municipio desde datos DERA locales
   * Busca el ayuntamiento como punto de referencia para fallback.
   * 
   * @param municipality - Nombre del municipio
   * @param codMun - Código INE opcional para mayor precisión
   * @returns Coordenadas UTM30 del ayuntamiento o null
   */
  private async getMunicipalityCentroid(
    municipality: string,
    codMun?: string
  ): Promise<{ x: number; y: number; name: string } | null> {
    try {
      // Buscar ayuntamiento en datos locales DERA
      const result = await searchLocal({
        nombre: `Ayuntamiento de ${municipality}`,
        municipio: municipality,
        codMun,
        categorias: ['municipal'],
        maxResults: 1,
        threshold: 0.3 // Umbral bajo para encontrar variantes
      });

      if (result.success && result.bestMatch) {
        return {
          x: result.bestMatch.x,
          y: result.bestMatch.y,
          name: result.bestMatch.nombre
        };
      }

      // Fallback: buscar cualquier infraestructura municipal
      const fallback = await searchLocal({
        nombre: municipality,
        municipio: municipality,
        codMun,
        categorias: ['municipal'],
        maxResults: 1,
        threshold: 0.2
      });

      if (fallback.success && fallback.bestMatch) {
        return {
          x: fallback.bestMatch.x,
          y: fallback.bestMatch.y,
          name: fallback.bestMatch.nombre
        };
      }

      return null;
    } catch (error) {
      console.error(`[TorresComGeocoder] Error obteniendo centroide de ${municipality}:`, error);
      return null;
    }
  }

  /**
   * Estadísticas del geocodificador
   */
  public override getStats(): {
    cacheSize: number;
    endpoint: string;
    layer: string;
    searchIndexSize: number;
    currentIndexMunicipality: string | null;
    availableSources: string[];
  } {
    const baseStats = super.getStats();
    return {
      ...baseStats,
      availableSources: ['DERA_G9', 'OSM_OVERPASS', 'NGA_TOPONYM']
    };
  }

  // ==========================================================================
  // F3.T3.3 - DISTRIBUCIÓN INTELIGENTE DE MÚLTIPLES ANTENAS
  // ==========================================================================

  /**
   * F3.T3.3: Geocodifica múltiples infraestructuras TELECOM con distribución inteligente
   * 
   * Estrategia:
   * 1. Obtener todas las torres OSM del municipio (una sola llamada)
   * 2. Si hay direcciones/referencias, intentar geocodificar por proximidad
   * 3. Distribuir torres disponibles entre infraestructuras usando round-robin
   * 4. Marcar ubicaciones compartidas cuando N infraestructuras > M torres
   * 
   * @param infrastructures - Array de infraestructuras TELECOM del documento PTEL
   * @param municipality - Nombre del municipio
   * @param province - Provincia (opcional)
   * @param codMun - Código INE del municipio (opcional)
   * @returns Array de resultados en el mismo orden que las infraestructuras de entrada
   * 
   * @example
   * ```typescript
   * const results = await geocoder.geocodeMobileDistributed(
   *   [
   *     { id: '1', name: 'Antena Movistar', address: 'Cerro del Castillo' },
   *     { id: '2', name: 'Antena Vodafone' },
   *     { id: '3', name: 'Repetidor Municipal' }
   *   ],
   *   'Colomera',
   *   'Granada'
   * );
   * // Cada resultado indica si es compartido y con quién
   * ```
   */
  public async geocodeMobileDistributed(
    infrastructures: TelecomInfrastructure[],
    municipality: string,
    province?: string,
    codMun?: string
  ): Promise<DistributedGeocodingResult[]> {
    if (!infrastructures || infrastructures.length === 0) {
      return [];
    }

    if (!municipality) {
      console.warn('[TorresComGeocoder] Municipio requerido para distribución');
      return infrastructures.map(infra => this.createFallbackResult(infra, municipality, province));
    }

    try {
      // === PASO 1: Obtener todas las torres del municipio (una sola vez) ===
      const allTowers = await this.osmGeocoder.findAllTelecomInfrastructure(
        municipality,
        province
      );

      // Filtrar torres móviles vs todas
      const mobileTowers = allTowers.filter(tower => {
        const category = this.osmGeocoder.classifyTelecomType(tower);
        return category === 'MOBILE' || category === 'UNKNOWN';
      });

      const towersToUse = mobileTowers.length > 0 ? mobileTowers : allTowers;
      
      console.log(
        `[TorresComGeocoder] Distribución: ${infrastructures.length} infraestructuras, ` +
        `${towersToUse.length} torres disponibles (${mobileTowers.length} móviles de ${allTowers.length} total)`
      );

      // === PASO 2: Obtener centroide para fallback ===
      const centroid = await this.getMunicipalityCentroid(municipality, codMun);

      // === PASO 3: Intentar geocodificación sofisticada por dirección/referencia ===
      const addressMatches = await this.matchByAddressProximity(
        infrastructures,
        towersToUse,
        municipality,
        province
      );

      // === PASO 4: Distribuir torres restantes con round-robin ===
      const results: DistributedGeocodingResult[] = [];
      const assignedTowerIds = new Set<number>();
      const towerAssignments = new Map<number, string[]>(); // osmId → [infraIds]
      let assignmentOrder = 0;

      for (const infra of infrastructures) {
        assignmentOrder++;
        
        // Caso A: Ya tiene match por dirección
        const addressMatch = addressMatches.get(infra.id);
        if (addressMatch) {
          results.push(addressMatch);
          if (addressMatch.osmId) {
            assignedTowerIds.add(addressMatch.osmId);
            this.trackTowerAssignment(towerAssignments, addressMatch.osmId, infra.id);
          }
          continue;
        }

        // Caso B: Asignar torre no usada (distribución)
        const availableTower = towersToUse.find(t => !assignedTowerIds.has(t.id));
        
        if (availableTower) {
          assignedTowerIds.add(availableTower.id);
          this.trackTowerAssignment(towerAssignments, availableTower.id, infra.id);
          
          const { x, y } = this.osmGeocoder.toUTM30(availableTower.lon, availableTower.lat);
          
          results.push({
            x,
            y,
            confidence: 55,
            source: 'OpenStreetMap (distribuido)',
            matchedName: availableTower.name || 'Torre sin nombre',
            matchType: 'distributed_L1',
            municipality,
            province: province || '',
            towerType: TowerType.MOBILE,
            dataSource: 'OSM_OVERPASS',
            osmId: availableTower.id,
            assignmentOrder,
            sharedLocation: false,
            metadata: {
              osmId: availableTower.id,
              infraId: infra.id, // F3.T3.3: Para filtrar correctamente en sharedWith
              originalSearch: infra.name,
              approximationLevel: 'L1_DISTRIBUTED',
              approximationNote: `Torre asignada por distribución: "${availableTower.name || 'sin nombre'}". ` +
                `Verificar correspondencia con ${infra.name}.`,
              assignmentOrder,
              totalTowers: towersToUse.length,
              totalInfrastructures: infrastructures.length,
              tags: availableTower.tags
            }
          });
          continue;
        }

        // Caso C: Todas las torres asignadas → reusar con round-robin
        if (towersToUse.length > 0) {
          const towerIndex = (assignmentOrder - 1) % towersToUse.length;
          const reusedTower = towersToUse[towerIndex];
          this.trackTowerAssignment(towerAssignments, reusedTower.id, infra.id);
          
          const { x, y } = this.osmGeocoder.toUTM30(reusedTower.lon, reusedTower.lat);
          
          results.push({
            x,
            y,
            confidence: 45, // Menor porque es compartida
            source: 'OpenStreetMap (compartido)',
            matchedName: reusedTower.name || 'Torre sin nombre',
            matchType: 'shared_L1',
            municipality,
            province: province || '',
            towerType: TowerType.MOBILE,
            dataSource: 'OSM_OVERPASS',
            osmId: reusedTower.id,
            assignmentOrder,
            sharedLocation: true,
            metadata: {
              osmId: reusedTower.id,
              infraId: infra.id, // F3.T3.3: Para filtrar correctamente en sharedWith
              originalSearch: infra.name,
              approximationLevel: 'L1_SHARED',
              approximationNote: `Ubicación compartida con otras antenas. ` +
                `Torre: "${reusedTower.name || 'sin nombre'}". Verificar in-situ.`,
              assignmentOrder,
              warning: 'UBICACION_COMPARTIDA',
              tags: reusedTower.tags
            }
          });
          continue;
        }

        // Caso D: Sin torres → centroide
        if (centroid) {
          results.push({
            x: centroid.x,
            y: centroid.y,
            confidence: 35,
            source: 'DERA Municipal (centroide)',
            matchedName: centroid.name,
            matchType: 'centroid_fallback_L3',
            municipality,
            province: province || '',
            towerType: TowerType.GENERIC,
            dataSource: 'DERA_G9',
            assignmentOrder,
            sharedLocation: true,
            metadata: {
              infraId: infra.id, // F3.T3.3: Para consistencia con otros casos
              originalSearch: infra.name,
              approximationLevel: 'L3_CENTROID_FALLBACK',
              approximationNote: `Sin torres telecom en OSM. Coordenadas del centro urbano (${centroid.name}). ` +
                `REQUIERE VERIFICACIÓN IN-SITU.`,
              centroidSource: centroid.name,
              centroidCoords: { x: centroid.x, y: centroid.y },
              warning: 'BAJA_PRECISION',
              assignmentOrder
            }
          });
        } else {
          // Caso E: Sin centroide → null result placeholder
          results.push(this.createFallbackResult(infra, municipality, province));
        }
      }

      // === PASO 5: Actualizar sharedWith en resultados ===
      this.updateSharedWithInfo(results, towerAssignments, infrastructures);

      return results;

    } catch (error) {
      console.error('[TorresComGeocoder] Error en distribución:', error);
      return infrastructures.map(infra => this.createFallbackResult(infra, municipality, province));
    }
  }

  /**
   * F3.T3.3: Intenta geocodificar por dirección/referencia y proximidad a torres
   * 
   * Si una infraestructura tiene dirección ("Cerro del Castillo"), intenta:
   * 1. Geocodificar la dirección con CartoCiudad/Nominatim
   * 2. Buscar la torre más cercana a esas coordenadas
   * 3. Si está a <500m, asignar esa torre con alta confianza
   * 
   * @param infrastructures - Infraestructuras a procesar
   * @param towers - Torres disponibles en el municipio
   * @param municipality - Municipio
   * @param province - Provincia
   * @returns Mapa de infraId → resultado (solo para las que tienen match)
   */
  private async matchByAddressProximity(
    infrastructures: TelecomInfrastructure[],
    towers: Array<{ id: number; name: string; lat: number; lon: number; tags: Record<string, string> }>,
    municipality: string,
    province?: string
  ): Promise<Map<string, DistributedGeocodingResult>> {
    const matches = new Map<string, DistributedGeocodingResult>();
    
    if (towers.length === 0) {
      return matches;
    }

    // Filtrar infraestructuras con dirección/referencia
    const withAddress = infrastructures.filter(i => i.address && i.address.trim().length > 3);
    
    if (withAddress.length === 0) {
      return matches;
    }

    console.log(
      `[TorresComGeocoder] Intentando geocodificación por dirección para ${withAddress.length} infraestructuras`
    );

    for (const infra of withAddress) {
      try {
        // Intentar geocodificar la dirección/referencia
        const addressCoords = await this.geocodeAddressReference(
          infra.address!,
          municipality,
          province
        );

        if (!addressCoords) {
          continue;
        }

        // Buscar torre más cercana a las coordenadas geocodificadas
        const nearestTower = this.findNearestTower(addressCoords, towers);

        if (nearestTower && nearestTower.distance < 500) {
          // Torre a menos de 500m → match con alta confianza
          const { x, y } = this.osmGeocoder.toUTM30(nearestTower.tower.lon, nearestTower.tower.lat);
          
          console.log(
            `[TorresComGeocoder] Match por dirección: "${infra.name}" (${infra.address}) → ` +
            `"${nearestTower.tower.name}" a ${nearestTower.distance.toFixed(0)}m`
          );

          matches.set(infra.id, {
            x,
            y,
            confidence: 70, // Alta confianza por match geográfico
            source: 'OpenStreetMap (match por dirección)',
            matchedName: nearestTower.tower.name || 'Torre sin nombre',
            matchType: 'address_proximity_match',
            municipality,
            province: province || '',
            towerType: TowerType.MOBILE,
            dataSource: 'OSM_OVERPASS',
            osmId: nearestTower.tower.id,
            sharedLocation: false,
            distanceToMatchedTower: nearestTower.distance,
            metadata: {
              osmId: nearestTower.tower.id,
              infraId: infra.id, // F3.T3.3: Para filtrar correctamente en sharedWith
              originalSearch: infra.name,
              originalAddress: infra.address,
              approximationLevel: 'L0_ADDRESS_MATCH',
              approximationNote: `Torre "${nearestTower.tower.name || 'sin nombre'}" encontrada a ${nearestTower.distance.toFixed(0)}m ` +
                `de "${infra.address}". Alta probabilidad de correspondencia.`,
              distanceToAddress: nearestTower.distance,
              geocodedAddress: addressCoords,
              tags: nearestTower.tower.tags
            }
          });
        } else if (nearestTower && nearestTower.distance < 2000) {
          // Torre a menos de 2km → match con confianza media
          const { x, y } = this.osmGeocoder.toUTM30(nearestTower.tower.lon, nearestTower.tower.lat);
          
          console.log(
            `[TorresComGeocoder] Match aproximado por dirección: "${infra.name}" (${infra.address}) → ` +
            `"${nearestTower.tower.name}" a ${nearestTower.distance.toFixed(0)}m`
          );

          matches.set(infra.id, {
            x,
            y,
            confidence: 55,
            source: 'OpenStreetMap (proximidad dirección)',
            matchedName: nearestTower.tower.name || 'Torre sin nombre',
            matchType: 'address_proximity_approximate',
            municipality,
            province: province || '',
            towerType: TowerType.MOBILE,
            dataSource: 'OSM_OVERPASS',
            osmId: nearestTower.tower.id,
            sharedLocation: false,
            distanceToMatchedTower: nearestTower.distance,
            metadata: {
              osmId: nearestTower.tower.id,
              infraId: infra.id, // F3.T3.3: Para filtrar correctamente en sharedWith
              originalSearch: infra.name,
              originalAddress: infra.address,
              approximationLevel: 'L1_ADDRESS_APPROXIMATE',
              approximationNote: `Torre "${nearestTower.tower.name || 'sin nombre'}" a ${nearestTower.distance.toFixed(0)}m ` +
                `de "${infra.address}". Verificar correspondencia.`,
              distanceToAddress: nearestTower.distance,
              geocodedAddress: addressCoords,
              tags: nearestTower.tower.tags
            }
          });
        }
      } catch (error) {
        console.warn(`[TorresComGeocoder] Error geocodificando dirección "${infra.address}":`, error);
      }
    }

    return matches;
  }

  /**
   * Geocodifica una referencia de dirección usando Nominatim
   */
  private async geocodeAddressReference(
    address: string,
    municipality: string,
    province?: string
  ): Promise<{ x: number; y: number; lat: number; lon: number } | null> {
    try {
      // Usar Nominatim a través de OverpassGeocoder
      const result = await this.osmGeocoder.geocodeWithNominatim(
        address,
        municipality,
        province
      );
      
      if (result && result.x && result.y) {
        // Convertir UTM30 a WGS84 para cálculos de distancia
        const { lat, lon } = this.osmGeocoder.toWGS84(result.x, result.y);
        return {
          x: result.x,
          y: result.y,
          lat,
          lon
        };
      }
      
      return null;
    } catch (error) {
      console.warn(`[TorresComGeocoder] Error en geocodeAddressReference:`, error);
      return null;
    }
  }

  /**
   * Encuentra la torre más cercana a unas coordenadas
   */
  private findNearestTower(
    coords: { lat: number; lon: number },
    towers: Array<{ id: number; name: string; lat: number; lon: number; tags: Record<string, string> }>
  ): { tower: typeof towers[0]; distance: number } | null {
    if (towers.length === 0) {
      return null;
    }

    let nearest: typeof towers[0] | null = null;
    let minDistance = Infinity;

    for (const tower of towers) {
      const distance = this.haversineDistance(
        coords.lat, coords.lon,
        tower.lat, tower.lon
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearest = tower;
      }
    }

    return nearest ? { tower: nearest, distance: minDistance } : null;
  }

  /**
   * Calcula distancia Haversine entre dos puntos (en metros)
   */
  private haversineDistance(
    lat1: number, lon1: number,
    lat2: number, lon2: number
  ): number {
    const R = 6371000; // Radio de la Tierra en metros
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Registra asignación de torre para tracking de compartidos
   */
  private trackTowerAssignment(
    assignments: Map<number, string[]>,
    towerId: number,
    infraId: string
  ): void {
    const existing = assignments.get(towerId) || [];
    existing.push(infraId);
    assignments.set(towerId, existing);
  }

  /**
   * Actualiza sharedWith en los resultados basándose en las asignaciones
   */
  private updateSharedWithInfo(
    results: DistributedGeocodingResult[],
    assignments: Map<number, string[]>,
    infrastructures: TelecomInfrastructure[]
  ): void {
    // Crear mapa infraId → nombre para lookup rápido
    const infraNames = new Map(infrastructures.map(i => [i.id, i.name]));

    for (const result of results) {
      if (result.osmId) {
        const sharedIds = assignments.get(result.osmId) || [];
        if (sharedIds.length > 1) {
          result.sharedLocation = true;
          // F3.T3.3 FIX: Usar infraId (no originalSearch) para filtrar correctamente
          result.sharedWith = sharedIds
            .filter(id => id !== result.metadata?.infraId)
            .map(id => infraNames.get(id) || id);
          
          // Actualizar metadata
          if (result.metadata) {
            result.metadata.sharedWith = result.sharedWith;
            result.metadata.sharedCount = sharedIds.length;
          }
        }
      }
    }
  }

  /**
   * Crea resultado fallback cuando no hay datos disponibles
   */
  private createFallbackResult(
    infra: TelecomInfrastructure,
    municipality: string,
    province?: string
  ): DistributedGeocodingResult {
    return {
      x: 0,
      y: 0,
      confidence: 0,
      source: 'Sin datos',
      matchedName: '',
      matchType: 'no_match',
      municipality,
      province: province || '',
      towerType: TowerType.GENERIC,
      dataSource: 'DERA_G9',
      sharedLocation: false,
      metadata: {
        infraId: infra.id, // F3.T3.3: Para consistencia
        originalSearch: infra.name,
        approximationLevel: 'NO_DATA',
        approximationNote: `No se encontraron datos para geocodificar "${infra.name}". ` +
          `Introducir coordenadas manualmente.`,
        warning: 'SIN_GEOCODIFICACION'
      }
    };
  }
}
